<script lang="ts">
	import { onMount } from 'svelte';
	import type { IMarketData } from 'customTypes/marketData';
	import activeCurrency from 'stores/activeCurrency';
	import { _ } from 'utils/i18/i18n';
	import { getMarketData } from 'services/market-data/market-data';
	import { browser } from '$app/environment';
	import formatNumber from 'utils/formatNumber';
	import { memoizeValue } from 'utils/decorators';
	import SchemaOrgMarketOverview from 'components/SchemaOrg/SchemaOrgMarketOverview.svelte';

	export let dark = false;
	export let marketData: IMarketData;

	let data = prepareMarketData(marketData, $activeCurrency.currency);
	let marketOverviewWrapper = null;
	let scrollLeft = 0;
	let scrollWidth = 0;
	let clientWidth = 0;
	const scrollStep = 15;
	const leftScrollOffset = 70;
	const isCurrencyChanged = memoizeValue($activeCurrency.currency);

	const getData = async () => {
		if (!browser) return;
		try {
			marketData = await getMarketData($activeCurrency.currency.toUpperCase());
			data = prepareMarketData(marketData, $activeCurrency.currency);
		} catch (error) {
			console.log(error);
		}
	};

	function prepareMarketData(raw, currency) {
		return {
			marketCap: raw?.marketCap ? formatNumber(raw.marketCap, currency, false) : '-',
			'24HVol': raw?.dayVolume ? formatNumber(raw.dayVolume, currency, true) : '-',
			bTCDominance: raw?.btcDominance ? `${(raw.btcDominance * 100).toFixed(1)}%` : '-',
			cryptos: raw?.coinsAmount.toLocaleString('en-EN') || '-',
			exchanges: raw?.exchanges.toLocaleString('en-EN') || '-',
		};
	}

	const setScrollProperties = () => {
		if (!browser) return;

		scrollLeft = marketOverviewWrapper?.scrollLeft;
		scrollWidth = marketOverviewWrapper?.scrollWidth;
		clientWidth = marketOverviewWrapper?.clientWidth;
	};

	onMount(() => {
		setScrollProperties();
	});

	$: isCurrencyChanged($activeCurrency.currency) && getData();

	const onHandleLeft = () => {
		marketOverviewWrapper.scrollLeft += scrollStep;
	};

	const onHandleRight = () => {
		marketOverviewWrapper.scrollLeft -= scrollStep;
	};
</script>

<SchemaOrgMarketOverview {data} />
<div class="market-overview container" class:dark>
	<div class="overview-wrapper">
		<div class="list-wrapper">
			<button
				type="button"
				class="btn-arrow left-btn"
				aria-label="Left"
				style={scrollLeft >= leftScrollOffset ? 'opacity: 1' : 'opacity: 0'}
				on:click={onHandleRight}
			>
				<svg width="22" height="15" viewBox="0 0 24 13" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M 1.069 5.992 C 0.894 6.168 0.894 6.414 1.069 6.59 L 6.237 11.793 C 6.378 11.969 6.659 11.969 6.835 11.793 L 7.503 11.09 C 7.679 10.914 7.679 10.668 7.503 10.492 L 3.355 6.274 L 7.503 2.09 C 7.679 1.914 7.679 1.633 7.503 1.492 L 6.835 0.789 C 6.659 0.613 6.378 0.613 6.237 0.789 L 1.069 5.992 Z"
						fill={!dark ? 'black' : 'white'}
						fill-opacity="0.2"
					/>
				</svg>
			</button>

			<!-- svelte-ignore a11y-no-static-element-interactions -->
			<div
				class="scrollable"
				bind:this={marketOverviewWrapper}
				on:scroll={setScrollProperties}
				on:mousemove={setScrollProperties}
			>
				<ul class="market-overview-list">
					{#each Object.entries(data) as [name, value]}
						<li>
							<span class="item-label">{$_(`marketOverview.${name}`)}</span>
							<span class="item-value">
								{value}
							</span>
						</li>
					{/each}
				</ul>
			</div>

			<button
				type="button"
				class="btn-arrow right-btn"
				aria-label="Right"
				style={scrollLeft + clientWidth === scrollWidth ? 'opacity: 0' : 'opacity: 1'}
				on:click={onHandleLeft}
			>
				<svg width="22" height="15" viewBox="0 0 24 13" xmlns="http://www.w3.org/2000/svg">
					<path
						d="M 16.616 5.992 C 16.44 6.168 16.44 6.414 16.616 6.59 L 21.784 11.793 C 21.925 11.969 22.206 11.969 22.382 11.793 L 23.05 11.09 C 23.226 10.914 23.226 10.668 23.05 10.492 L 18.901 6.274 L 23.05 2.09 C 23.226 1.914 23.226 1.633 23.05 1.492 L 22.382 0.789 C 22.206 0.613 21.925 0.613 21.784 0.789 L 16.616 5.992 Z"
						fill={!dark ? 'black' : 'white'}
						fill-opacity="0.2"
						transform="matrix(-1, 0, 0, -1, 39.666, 12.582001)"
					/>
				</svg>
			</button>
		</div>
	</div>
</div>

<style lang="scss">
	@import 'marketOverview';
</style>
